import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { cardsApi } from '@/features/cards/api'
import { getErrorMessage } from '@/api/errors'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { Card } from '@/types/entities'
import { MatrixPicker, QUADRANT_CHIP } from './MatrixPicker'
import { quadrantOf, withMatrix, type Quadrant } from './quadrants'

/** Saves the answer on the item. Used by the chip, the drawer and the "before you start" prompt. */
export function useSetMatrix(card: { id: string; customAttrs?: Record<string, unknown> | null }) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (next: Quadrant | null) =>
      cardsApi.patch(card.id, { customAttrs: withMatrix(card.customAttrs, next ? { urgent: next.urgent, important: next.important } : null) }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['card', card.id], updated)
      void queryClient.invalidateQueries({ queryKey: ['cards'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

/**
 * The item's place in the urgent/important matrix, as a label that opens the picker.
 * Unset shows nothing on the board; `alwaysShow` (the drawer) offers a dashed "Set priority" instead.
 */
export function MatrixChip({ card, editable, alwaysShow = false, className }: { card: Card; editable: boolean; alwaysShow?: boolean; className?: string }) {
  const [open, setOpen] = useState(false)
  const set = useSetMatrix(card)
  const q = quadrantOf(card.customAttrs)
  if (!q && !(alwaysShow && editable)) return null

  const chip = (
    <span
      className={cn(
        'inline-flex h-5 items-center rounded-sm px-2 text-micro font-semibold',
        q ? QUADRANT_CHIP[q.key] : 'border border-dashed border-border text-text-muted',
        editable && 'cursor-pointer',
        className,
      )}
      title={q ? `${q.title}: ${q.short}` : 'Urgent? Important? Not answered yet.'}
    >
      {q ? q.title : 'Set priority'}
    </span>
  )
  if (!editable) return chip
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-no-dnd className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Set urgent and important">
          {chip}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[26rem] max-w-[92vw]" data-no-dnd>
        <MatrixPicker
          value={q}
          disabled={set.isPending}
          onChange={(next) => {
            set.mutate(next)
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
